import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { supabaseV2 } from "../../lib/supabase";
import { track } from "../../lib/analytics";
import { AuthShell } from "../../components/shell/AuthShell";

type AuthMode =
  | "login"
  | "signup"
  | "signup-pending"
  | "needs-confirm"
  | "resend"
  | "recovery"
  | "recovery-sent";

const PATH_TO_MODE: Record<string, AuthMode> = {
  "/login": "login",
  "/signup": "signup",
  "/recovery": "recovery",
  "/resend-confirmation": "resend",
};
const MODE_TO_PATH: Partial<Record<AuthMode, string>> = {
  login: "/login",
  signup: "/signup",
  recovery: "/recovery",
  resend: "/resend-confirmation",
};

export function AuthScreen() {
  const { t } = useTranslation("auth");
  const location = useLocation();
  const navigate = useNavigate();
  const initialMode: AuthMode = PATH_TO_MODE[location.pathname] || "login";
  const [mode, setModeState] = useState<AuthMode>(initialMode);
  // Sincronizza il mode quando cambia la URL (es. browser back/forward).
  // Importante: non sovrascrivere i mode transitori (signup-pending, needs-confirm,
  // recovery-sent) che non hanno path proprio e lasciano la URL al valore precedente.
  useEffect(() => {
    const next = PATH_TO_MODE[location.pathname];
    if (next && next !== mode && MODE_TO_PATH[mode]) setModeState(next);
  }, [location.pathname, mode]);
  const setMode = (next: AuthMode) => {
    setModeState(next);
    const path = MODE_TO_PATH[next];
    if (path && path !== location.pathname) navigate(path, { replace: true });
  };
  const [email, setEmail] = useState(() => {
    // Pre-fill da query string ?email=... usato dagli inviti ai provinanti.
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(window.location.search);
    return params.get("email")?.trim() || "";
  });
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const reset = (next: AuthMode) => {
    setMode(next);
    setError(null);
    setInfo(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setInfo(null);

    if (mode === "recovery") {
      const { error: recErr } = await supabaseV2.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/`,
      });
      setLoading(false);
      if (recErr) {
        setError(recErr.message);
        return;
      }
      setMode("recovery-sent");
      return;
    }

    if (mode === "signup") {
      const fName = firstName.trim();
      const lName = lastName.trim();
      if (!fName || !lName) {
        setLoading(false);
        setError(t("errors.missingName"));
        return;
      }
      const { error: signUpError } = await supabaseV2.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            first_name: fName,
            last_name: lName,
            full_name: `${fName} ${lName}`.trim(),
          },
        },
      });
      setLoading(false);
      if (signUpError) {
        setError(signUpError.message);
        return;
      }
      // GA4 standard event "sign_up" — usato per il funnel di acquisizione.
      track.signUp({ source: "auth_screen" });
      setMode("signup-pending");
      return;
    }

    const { error: loginError } = await supabaseV2.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (loginError) {
      const msg = (loginError.message || "").toLowerCase();
      const code = (loginError as any).code || "";
      if (msg.includes("not confirmed") || msg.includes("email not confirmed") || code === "email_not_confirmed") {
        setMode("needs-confirm");
      } else {
        setError(loginError.message);
      }
    }
  };

  const resendConfirmation = async () => {
    if (!email.trim()) {
      setError(t("errors.missingEmail"));
      return;
    }
    setResending(true);
    setError(null);
    setInfo(null);
    const { error: resendErr } = await supabaseV2.auth.resend({
      type: "signup",
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    setResending(false);
    if (resendErr) setError(resendErr.message);
    else setInfo(t("info.confirmSent"));
  };

  return (
    <AuthShell>
      <Card className="bg-transparent animate-fade-in">
        <CardHeader className="p-0 pb-5 space-y-2">
          <CardTitle className="text-[26px] font-bold leading-tight">
            {mode === "login" && t("titles.login")}
            {mode === "signup" && t("titles.signup")}
            {mode === "signup-pending" && t("titles.signupPending")}
            {mode === "needs-confirm" && t("titles.needsConfirm")}
            {mode === "resend" && t("titles.resend")}
            {mode === "recovery" && t("titles.recovery")}
            {mode === "recovery-sent" && t("titles.recoverySent")}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {mode === "login" && t("subtitles.login")}
            {mode === "signup" && t("subtitles.signup")}
            {mode === "signup-pending" && t("subtitles.signupPending", { email })}
            {mode === "needs-confirm" && (
              email
                ? t("subtitles.needsConfirmWithEmail", { email })
                : t("subtitles.needsConfirmNoEmail")
            )}
            {mode === "resend" && t("subtitles.resend")}
            {mode === "recovery" && t("subtitles.recovery")}
            {mode === "recovery-sent" && t("subtitles.recoverySent", { email: email || "" })}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {mode === "signup-pending" && (
            <div className="space-y-3">
              {info && (
                <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">{info}</div>
              )}
              {error && (
                <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">{error}</div>
              )}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={resendConfirmation}
                disabled={resending}
              >
                {resending ? t("buttons.sending") : t("buttons.resendConfirm")}
              </Button>
              <Button type="button" variant="link" className="w-full" onClick={() => reset("login")}>
                {t("buttons.backToLogin")}
              </Button>
            </div>
          )}

          {mode === "resend" && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                resendConfirmation();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="auth-resend-email">{t("labels.email")}</Label>
                <Input
                  id="auth-resend-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              {info && (
                <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">{info}</div>
              )}
              {error && (
                <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">{error}</div>
              )}
              <Button type="submit" variant="hero" className="w-full" disabled={resending}>
                {resending ? t("buttons.sending") : t("buttons.sendConfirmLink")}
              </Button>
              <Button type="button" variant="link" className="w-full" onClick={() => reset("login")}>
                {t("buttons.backToLogin")}
              </Button>
            </form>
          )}

          {mode === "needs-confirm" && (
            <div className="space-y-3">
              {info && (
                <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">{info}</div>
              )}
              {error && (
                <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">{error}</div>
              )}
              <Button
                type="button"
                variant="hero"
                className="w-full"
                onClick={resendConfirmation}
                disabled={resending}
              >
                {resending ? t("buttons.sending") : t("buttons.resendConfirm")}
              </Button>
              <Button type="button" variant="link" className="w-full" onClick={() => reset("login")}>
                {t("buttons.backToLogin")}
              </Button>
            </div>
          )}

          {mode === "recovery-sent" && (
            <div className="space-y-3">
              {info && (
                <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">{info}</div>
              )}
              {error && (
                <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">{error}</div>
              )}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={resendConfirmation}
                disabled={resending}
                title={t("tooltips.resendIfNotConfirmed") || undefined}
              >
                {resending ? t("buttons.sending") : t("buttons.resendConfirmAccount")}
              </Button>
              <Button type="button" variant="link" className="w-full" onClick={() => reset("login")}>
                {t("buttons.backToLogin")}
              </Button>
            </div>
          )}

          {(mode === "login" || mode === "signup" || mode === "recovery") && (
            <>
              <form className="space-y-4" onSubmit={submit}>
                {mode === "signup" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="auth-first-name">{t("labels.firstName")}</Label>
                      <Input
                        id="auth-first-name"
                        type="text"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="auth-last-name">{t("labels.lastName")}</Label>
                      <Input
                        id="auth-last-name"
                        type="text"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="auth-email">{t("labels.email")}</Label>
                  <Input
                    id="auth-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                {mode !== "recovery" && (
                  <div className="space-y-2">
                    <Label htmlFor="auth-pwd">{t("labels.password")}</Label>
                    <Input
                      id="auth-pwd"
                      name={mode === "signup" ? "new-password" : "current-password"}
                      type="password"
                      autoComplete={mode === "signup" ? "new-password" : "current-password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      required
                    />
                    {mode === "signup" && (
                      <p className="text-[11px] text-muted-foreground">
                        {t("passwordHint")}
                      </p>
                    )}
                  </div>
                )}
                {error && (
                  <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">
                    {error}
                  </div>
                )}
                {info && (
                  <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {info}
                  </div>
                )}
                <Button type="submit" variant="hero" className="w-full" disabled={loading}>
                  {loading
                    ? t("buttons.waiting")
                    : mode === "login"
                      ? t("buttons.login")
                      : mode === "signup"
                        ? t("buttons.signup")
                        : t("buttons.recovery")}
                </Button>
                {mode === "recovery" ? (
                  <Button type="button" variant="link" className="w-full" onClick={() => reset("login")}>
                    {t("buttons.backToLogin")}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    className="w-full"
                    onClick={() => reset(mode === "login" ? "signup" : "login")}
                  >
                    {mode === "login" ? t("buttons.noAccount") : t("buttons.haveAccount")}
                  </Button>
                )}
                {mode === "login" && (
                  <>
                    <button
                      type="button"
                      onClick={() => reset("recovery")}
                      className="block w-full text-center text-[13px] font-medium text-foreground hover:text-rose-500 hover:underline"
                    >
                      {t("buttons.forgotPassword")}
                    </button>
                    <button
                      type="button"
                      onClick={() => reset("resend")}
                      className="block w-full text-center text-[12px] font-medium text-muted-foreground hover:text-foreground hover:underline mt-1"
                    >
                      {t("buttons.noConfirmEmail")}
                    </button>
                  </>
                )}
              </form>

              {mode === "signup" && (
                <ul className="mt-4 pt-4 border-t space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-accent" /> {t("signupPerks.perk1")}</li>
                  <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-accent" /> {t("signupPerks.perk2")}</li>
                  <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-accent" /> {t("signupPerks.perk3")}</li>
                </ul>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
